import React, {
    useEffect,
    useRef,
    useState,
  } from "react";
  
  import {
    createChart,
    CandlestickSeries,
    BarSeries,
    AreaSeries,
    CrosshairMode,
  } from "lightweight-charts";
  
  import "./CandlestickChart.css";
  
  const CandlestickChart = ({
    candles = [],
    timeframe = "5m",
    chartType = "candles",
    showOHLC = false,
  }) => {
    const containerRef =
      useRef(null);
  
    const chartRef =
      useRef(null);
  
    const seriesRef =
      useRef(null);
  
    const firstLoadRef =
      useRef(true);
  
    const candlesRef =
      useRef(candles);
  
    const [
      selectedOHLC,
      setSelectedOHLC,
    ] = useState(null);
  
    // ==========================================
    // KEEP LATEST CANDLES
    // ==========================================
  
    useEffect(() => {
      candlesRef.current =
        candles;
    }, [candles]);
  
    // ==========================================
    // RESET FIT
    // ==========================================
  
    useEffect(() => {
      firstLoadRef.current =
        true;
  
      setSelectedOHLC(
        null
      );
    }, [
      timeframe,
      chartType,
    ]);
  
    // ==========================================
    // CREATE CHART
    // ==========================================
  
    useEffect(() => {
      if (
        !containerRef.current
      ) {
        return;
      }
  
      const container =
        containerRef.current;
  
      const chart =
        createChart(
          container,
          {
            width:
              container.clientWidth,
  
            height:
              container.clientHeight ||
              430,
  
            attributionLogo:
              false,
  
            layout: {
              background: {
                color:
                  "#080b12",
              },
  
              textColor:
                "#aab2c0",
  
              fontSize: 12,
  
              attributionLogo:
                false,
            },
  
            grid: {
              vertLines: {
                color:
                  "#252a35",
  
                style: 1,
              },
  
              horzLines: {
                color:
                  "#252a35",
  
                style: 1,
              },
            },
  
            rightPriceScale: {
              visible: true,
  
              borderVisible:
                true,
  
              borderColor:
                "#343b48",
  
              autoScale: true,
  
              scaleMargins: {
                top: 0.08,
                bottom: 0.08,
              },
  
              minimumWidth: 62,
            },
  
            leftPriceScale: {
              visible: false,
            },
  
            timeScale: {
              visible: true,
  
              borderVisible:
                true,
  
              borderColor:
                "#343b48",
  
              timeVisible:
                true,
  
              secondsVisible:
                false,
  
              rightOffset: 2,
  
              barSpacing: 7,
  
              minBarSpacing: 2,
  
              fixLeftEdge:
                false,
  
              fixRightEdge:
                false,
  
              lockVisibleTimeRangeOnResize:
                false,
  
              tickMarkFormatter:
                (time) => {
                  if (
                    typeof time !==
                    "number"
                  ) {
                    return "";
                  }
  
                  const date =
                    new Date(
                      time *
                        1000
                    );
  
                  const day =
                    String(
                      date.getDate()
                    ).padStart(
                      2,
                      "0"
                    );
  
                  const month =
                    String(
                      date.getMonth() +
                        1
                    ).padStart(
                      2,
                      "0"
                    );
  
                  const hour =
                    String(
                      date.getHours()
                    ).padStart(
                      2,
                      "0"
                    );
  
                  const minute =
                    String(
                      date.getMinutes()
                    ).padStart(
                      2,
                      "0"
                    );
  
                  return `${day}/${month} ${hour}:${minute}`;
                },
            },
  
            crosshair: {
              mode:
                CrosshairMode.Normal,
  
              vertLine: {
                visible: true,
                labelVisible:
                  true,
                color:
                  "#657080",
                width: 1,
                style: 2,
              },
  
              horzLine: {
                visible: true,
                labelVisible:
                  true,
                color:
                  "#657080",
                width: 1,
                style: 2,
              },
            },
  
            handleScroll: {
              mouseWheel: true,
  
              pressedMouseMove:
                true,
  
              horzTouchDrag:
                true,
  
              vertTouchDrag:
                true,
            },
  
            handleScale: {
              axisPressedMouseMove:
                true,
  
              mouseWheel: true,
  
              pinch: true,
            },
          }
        );
  
      // ========================================
      // CREATE SERIES
      // ========================================
  
      let series;
  
      if (
        chartType ===
        "candles"
      ) {
        series =
          chart.addSeries(
            CandlestickSeries,
            {
              upColor:
                "#00d6b2",
  
              downColor:
                "#ff594d",
  
              borderVisible:
                true,
  
              borderUpColor:
                "#00d6b2",
  
              borderDownColor:
                "#ff594d",
  
              wickUpColor:
                "#00d6b2",
  
              wickDownColor:
                "#ff594d",
  
              lastValueVisible:
                true,
  
              priceLineVisible:
                true,
  
              priceLineWidth:
                1,
  
              priceLineStyle:
                2,
  
              priceFormat: {
                type: "price",
  
                precision: 5,
  
                minMove:
                  0.00001,
              },
            }
          );
      } else if (
        chartType ===
        "bars"
      ) {
        series =
          chart.addSeries(
            BarSeries,
            {
              upColor:
                "#00d6b2",
  
              downColor:
                "#ff594d",
  
              thinBars:
                false,
  
              lastValueVisible:
                true,
  
              priceLineVisible:
                true,
  
              priceFormat: {
                type: "price",
  
                precision: 5,
  
                minMove:
                  0.00001,
              },
            }
          );
      } else {
        series =
          chart.addSeries(
            AreaSeries,
            {
              lineColor:
                "#00d6b2",
  
              topColor:
                "rgba(0, 214, 178, 0.35)",
  
              bottomColor:
                "rgba(0, 214, 178, 0.02)",
  
              lineWidth: 2,
  
              lastValueVisible:
                true,
  
              priceLineVisible:
                true,
  
              priceFormat: {
                type: "price",
  
                precision: 5,
  
                minMove:
                  0.00001,
              },
            }
          );
      }
  
      chartRef.current =
        chart;
  
      seriesRef.current =
        series;
  
      // ========================================
      // CROSSHAIR
      // ========================================
  
      const handleCrosshairMove =
        (param) => {
          if (
            !param ||
            param.time ===
              undefined
          ) {
            return;
          }
  
          // Candles / bars
          if (
            chartType ===
              "candles" ||
            chartType ===
              "bars"
          ) {
            const data =
              param.seriesData.get(
                series
              );
  
            if (!data) {
              return;
            }
  
            const open =
              Number(
                data.open
              );
  
            const high =
              Number(
                data.high
              );
  
            const low =
              Number(
                data.low
              );
  
            const close =
              Number(
                data.close
              );
  
            if (
              Number.isFinite(
                open
              ) &&
              Number.isFinite(
                high
              ) &&
              Number.isFinite(
                low
              ) &&
              Number.isFinite(
                close
              )
            ) {
              setSelectedOHLC({
                open,
                high,
                low,
                close,
              });
            }
  
            return;
          }
  
          // Area chart
          const selectedTime =
            Number(
              param.time
            );
  
          const candle =
            candlesRef.current.find(
              (item) =>
                Number(
                  item.time
                ) ===
                selectedTime
            );
  
          if (!candle) {
            return;
          }
  
          setSelectedOHLC({
            open:
              Number(
                candle.open
              ),
  
            high:
              Number(
                candle.high
              ),
  
            low:
              Number(
                candle.low
              ),
  
            close:
              Number(
                candle.close
              ),
          });
        };
  
      chart.subscribeCrosshairMove(
        handleCrosshairMove
      );
  
      // ========================================
      // RESIZE
      // ========================================
  
      const resizeObserver =
        new ResizeObserver(
          (entries) => {
            if (
              !entries.length
            ) {
              return;
            }
  
            const {
              width,
              height,
            } =
              entries[0]
                .contentRect;
  
            if (
              width <= 0 ||
              height <= 0
            ) {
              return;
            }
  
            chart.applyOptions({
              width,
              height,
            });
          }
        );
  
      resizeObserver.observe(
        container
      );
  
      // ========================================
      // CLEANUP
      // ========================================
  
      return () => {
        chart.unsubscribeCrosshairMove(
          handleCrosshairMove
        );
  
        resizeObserver.disconnect();
  
        chart.remove();
  
        chartRef.current =
          null;
  
        seriesRef.current =
          null;
      };
    }, [chartType]);
  
    // ==========================================
    // SET DATA
    // ==========================================
  
    useEffect(() => {
      if (
        !seriesRef.current ||
        !candles.length
      ) {
        return;
      }
  
      const formattedData =
        candles
          .map(
            (candle) => ({
              time:
                Number(
                  candle.time
                ),
  
              open:
                Number(
                  candle.open
                ),
  
              high:
                Number(
                  candle.high
                ),
  
              low:
                Number(
                  candle.low
                ),
  
              close:
                Number(
                  candle.close
                ),
            })
          )
  
          .filter(
            (candle) =>
              Number.isFinite(
                candle.time
              ) &&
              Number.isFinite(
                candle.open
              ) &&
              Number.isFinite(
                candle.high
              ) &&
              Number.isFinite(
                candle.low
              ) &&
              Number.isFinite(
                candle.close
              )
          )
  
          .sort(
            (a, b) =>
              a.time -
              b.time
          );
  
      if (
        !formattedData.length
      ) {
        return;
      }
  
      // ========================================
      // REMOVE DUPLICATES
      // ========================================
  
      const uniqueData =
        [];
  
      formattedData.forEach(
        (candle) => {
          const previous =
            uniqueData[
              uniqueData.length -
                1
            ];
  
          if (
            previous &&
            previous.time ===
              candle.time
          ) {
            uniqueData[
              uniqueData.length -
                1
            ] = candle;
          } else {
            uniqueData.push(
              candle
            );
          }
        }
      );
  
      // ========================================
      // SET DATA
      // ========================================
  
      if (
        chartType ===
        "area"
      ) {
        seriesRef.current.setData(
          uniqueData.map(
            (candle) => ({
              time:
                candle.time,
  
              value:
                candle.close,
            })
          )
        );
      } else {
        seriesRef.current.setData(
          uniqueData
        );
      }
  
      // ========================================
      // DEFAULT OHLC
      // ========================================
  
      const last =
        uniqueData[
          uniqueData.length -
            1
        ];
  
      setSelectedOHLC({
        open: last.open,
        high: last.high,
        low: last.low,
        close: last.close,
      });
  
      // ========================================
      // FIT ONLY WHEN NEEDED
      // ========================================
  
      if (
        firstLoadRef.current &&
        chartRef.current
      ) {
        chartRef.current
          .timeScale()
          .fitContent();
  
        chartRef.current
          .timeScale()
          .applyOptions({
            rightOffset: 2,
          });
  
        firstLoadRef.current =
          false;
      }
    }, [
      candles,
      timeframe,
      chartType,
    ]);
  
    // ==========================================
    // RETURN
    // ==========================================
  
    return (
      <div className="candle-chart-wrapper">
  
        <div
          ref={containerRef}
          className="candle-chart"
        />
  
        {showOHLC &&
          selectedOHLC && (
            <div className="ohlc-popup">
  
              <div>
                <span>
                  Open
                </span>
  
                <strong>
                  {selectedOHLC.open.toFixed(
                    5
                  )}
                </strong>
              </div>
  
              <div>
                <span>
                  High
                </span>
  
                <strong>
                  {selectedOHLC.high.toFixed(
                    5
                  )}
                </strong>
              </div>
  
              <div>
                <span>
                  Low
                </span>
  
                <strong>
                  {selectedOHLC.low.toFixed(
                    5
                  )}
                </strong>
              </div>
  
              <div>
                <span>
                  Close
                </span>
  
                <strong>
                  {selectedOHLC.close.toFixed(
                    5
                  )}
                </strong>
              </div>
  
            </div>
          )}
  
      </div>
    );
  };
  
  export default CandlestickChart;